export declare namespace EventCalendarTokenSections {
	interface Surface {
		background: string;
		borderColor: string;
		mutedColor: string;
		faintColor: string;
	}

	interface Weekday {
		color: string;
		background: string;
		fontWeight: string;
		fontSize: string;
	}

	interface Day {
		color: string;
		outColor: string;
		outBackground: string;
		weekendBackground: string;
		todayBackground: string;
		todayColor: string;
		fontWeight: string;
		fontSize: string;
	}

	interface Chip {
		fontSize: string;
		borderRadius: string;
		paddingY: string;
		paddingX: string;
		densePaddingY: string;
		densePaddingX: string;
		text: string;
		overdueRing: string;
		overdueMarker: string;
		danger: {
			background: string;
			border: string;
		};
		warn: {
			background: string;
			border: string;
		};
		success: {
			background: string;
			border: string;
		};
		contrast: {
			background: string;
			border: string;
		};
		secondary: {
			background: string;
			border: string;
		};
	}

	interface More {
		color: string;
		hoverColor: string;
		fontWeight: string;
		fontSize: string;
	}

	interface Agenda {
		dayHeader: {
			color: string;
			background: string;
			fontWeight: string;
			fontSize: string;
		};
		row: {
			hoverBackground: string;
			borderColor: string;
		};
		swatch: {
			danger: string;
			warn: string;
			success: string;
			contrast: string;
			secondary: string;
		};
		title: {
			color: string;
			fontWeight: string;
			fontSize: string;
		};
		subtitle: {
			color: string;
			fontSize: string;
		};
		remaining: {
			color: string;
			overdueColor: string;
			fontWeight: string;
			fontSize: string;
		};
		chevron: {
			color: string;
		};
	}

	interface Week {
		columnBorderColor: string;
		header: {
			background: string;
			todayBackground: string;
			weekdayColor: string;
			weekdayFontWeight: string;
			dayColor: string;
			todayColor: string;
		};
	}

	interface Mini {
		borderColor: string;
		header: {
			background: string;
			color: string;
			hoverColor: string;
			fontSize: string;
			fontWeight: string;
		};
		weekday: {
			color: string;
			fontSize: string;
			fontWeight: string;
		};
		day: {
			color: string;
			fontSize: string;
			regularFontSize: string;
			todayOutline: string;
		};
	}

	interface Heat {
		bucket0: string;
		bucket1: string;
		bucket2: string;
		bucket3: string;
		bucket4: string;
		bucket3Color: string;
		bucket4Color: string;
	}

	interface Empty {
		color: string;
		titleColor: string;
		titleFontSize: string;
		titleFontWeight: string;
	}

	interface Toolbar {
		borderColor: string;
		titleColor: string;
		titleFontSize: string;
		titleFontWeight: string;
	}

	interface ViewSwitch {
		trackBackground: string;
		buttonColor: string;
		activeBackground: string;
		activeColor: string;
		fontWeight: string;
		fontSize: string;
	}

	interface Legend {
		labelColor: string;
		fontSize: string;
		overdueColor: string;
	}

	interface QuickView {
		rowBorderColor: string;
		keyColor: string;
		valueColor: string;
		valueFontWeight: string;
		overdueColor: string;
	}
}

export type EventCalendarTokens = {
	surface: EventCalendarTokenSections.Surface;
	weekday: EventCalendarTokenSections.Weekday;
	day: EventCalendarTokenSections.Day;
	chip: EventCalendarTokenSections.Chip;
	more: EventCalendarTokenSections.More;
	agenda: EventCalendarTokenSections.Agenda;
	week: EventCalendarTokenSections.Week;
	mini: EventCalendarTokenSections.Mini;
	heat: EventCalendarTokenSections.Heat;
	empty: EventCalendarTokenSections.Empty;
	toolbar: EventCalendarTokenSections.Toolbar;
	viewSwitch: EventCalendarTokenSections.ViewSwitch;
	legend: EventCalendarTokenSections.Legend;
	quickView: EventCalendarTokenSections.QuickView;
};

export const surface: EventCalendarTokenSections.Surface = {
	background: '{content.background}',
	borderColor: '{content.border.color}',
	mutedColor: '{text.muted.color}',
	faintColor: '{surface.400}',
};

export const weekday: EventCalendarTokenSections.Weekday = {
	// Same dark color/weight as the mini-month month-name title (.event-calendar-mini-header)
	// so the full day names read dark and bold across views; font size stays larger here to
	// keep the full names legible.
	color: '{text.color}',
	background: '{surface.50}',
	fontWeight: '600',
	fontSize: '0.75rem',
};

export const day: EventCalendarTokenSections.Day = {
	color: '{text.muted.color}',
	outColor: '{surface.400}',
	outBackground: '{surface.50}',
	weekendBackground: '{surface.50}',
	todayBackground: '{primary.50}',
	todayColor: '{primary.color}',
	fontWeight: '700',
	fontSize: '0.8125rem',
};

export const chip: EventCalendarTokenSections.Chip = {
	fontSize: '0.75rem',
	borderRadius: '{border.radius.md}',
	paddingY: '0.1875rem',
	paddingX: '0.375rem',
	densePaddingY: '0.125rem',
	densePaddingX: '0.25rem',
	text: '{text.color}',
	overdueRing: '{red.300}',
	overdueMarker: '{red.500}',
	danger: {
		background: '{red.50}',
		border: '{red.500}',
	},
	warn: {
		background: '{yellow.50}',
		border: '{yellow.600}',
	},
	success: {
		background: '{green.50}',
		border: '{green.600}',
	},
	contrast: {
		background: '{surface.100}',
		border: '{surface.700}',
	},
	secondary: {
		background: '{surface.100}',
		border: '{surface.400}',
	},
};

export const more: EventCalendarTokenSections.More = {
	color: '{primary.color}',
	hoverColor: '{primary.hover.color}',
	fontWeight: '600',
	fontSize: '0.75rem',
};

export const agenda: EventCalendarTokenSections.Agenda = {
	dayHeader: {
		color: '{text.muted.color}',
		background: '{surface.50}',
		fontWeight: '700',
		fontSize: '0.8125rem',
	},
	row: {
		hoverBackground: '{surface.50}',
		borderColor: '{content.border.color}',
	},
	swatch: {
		danger: '{red.500}',
		warn: '{yellow.600}',
		success: '{green.600}',
		contrast: '{surface.700}',
		secondary: '{surface.400}',
	},
	title: {
		color: '{text.color}',
		fontWeight: '600',
		fontSize: '0.875rem',
	},
	subtitle: {
		color: '{text.muted.color}',
		fontSize: '0.8125rem',
	},
	remaining: {
		color: '{text.muted.color}',
		overdueColor: '{red.500}',
		fontWeight: '600',
		fontSize: '0.8125rem',
	},
	chevron: {
		color: '{surface.400}',
	},
};

export const week: EventCalendarTokenSections.Week = {
	columnBorderColor: '{content.border.color}',
	header: {
		background: '{surface.50}',
		todayBackground: '{primary.50}',
		// Same dark color/weight as the mini-month month-name title (.event-calendar-mini-header)
		// so the full weekday names read dark and bold, matching the month view.
		weekdayColor: '{text.color}',
		weekdayFontWeight: '600',
		dayColor: '{text.color}',
		todayColor: '{primary.color}',
	},
};

export const mini: EventCalendarTokenSections.Mini = {
	borderColor: '{content.border.color}',
	header: {
		background: '{surface.50}',
		color: '{text.color}',
		hoverColor: '{primary.hover.color}',
		fontSize: '0.8125rem',
		fontWeight: '600',
	},
	weekday: {
		color: '{surface.400}',
		fontSize: '0.5625rem',
		fontWeight: '700',
	},
	day: {
		color: '{text.color}',
		fontSize: '0.6875rem',
		regularFontSize: '0.8125rem',
		todayOutline: '{primary.color}',
	},
};

export const heat: EventCalendarTokenSections.Heat = {
	// Density buckets: 0 / 1-2 / 3-4 / 5-6 / 7+. Steps from neutral to primary.
	bucket0: '{surface.100}',
	bucket1: '{primary.100}',
	bucket2: '{primary.200}',
	bucket3: '{primary.300}',
	bucket4: '{primary.color}',
	bucket3Color: '{primary.contrast.color}',
	bucket4Color: '{primary.contrast.color}',
};

export const empty: EventCalendarTokenSections.Empty = {
	color: '{surface.400}',
	titleColor: '{text.muted.color}',
	titleFontSize: '0.9375rem',
	titleFontWeight: '600',
};

export const toolbar: EventCalendarTokenSections.Toolbar = {
	borderColor: '{content.border.color}',
	titleColor: '{text.color}',
	titleFontSize: '1.0625rem',
	titleFontWeight: '700',
};

export const viewSwitch: EventCalendarTokenSections.ViewSwitch = {
	trackBackground: '{surface.100}',
	buttonColor: '{text.muted.color}',
	activeBackground: '{content.background}',
	activeColor: '{text.color}',
	fontWeight: '600',
	fontSize: '0.8125rem',
};

export const legend: EventCalendarTokenSections.Legend = {
	labelColor: '{text.muted.color}',
	fontSize: '0.8125rem',
	overdueColor: '{red.500}',
};

export const quickView: EventCalendarTokenSections.QuickView = {
	rowBorderColor: '{content.border.color}',
	keyColor: '{text.muted.color}',
	valueColor: '{text.color}',
	valueFontWeight: '600',
	overdueColor: '{red.500}',
};

/**
 * Event calendar styling tokens — color-coded chips, day cells, agenda rows,
 * mini-month grids and the year density heatmap. The chip and agenda swatch
 * status colors are keyed by the event status severity (eventStatusSeverity:
 * NotCompleted -> secondary, Assigned -> warn, InProgress -> success,
 * Completed -> contrast, Cancelled -> danger) using semantic surface/text
 * tokens. Overdue emphasis is a danger ring, independent
 * of the status color.
 */
export default {
	surface,
	weekday,
	day,
	chip,
	more,
	agenda,
	week,
	mini,
	heat,
	empty,
	toolbar,
	viewSwitch,
	legend,
	quickView,
} satisfies EventCalendarTokens;
